import React, { createContext, useContext, ReactNode } from 'react'

interface PreviewContextValue {
  windowWidth: number
  windowHeight: number
  isDesktop: boolean
  previewMode: 'desktop' | 'mobile'
}

const PreviewContext = createContext<PreviewContextValue | null>(null)

interface PreviewProviderProps {
  children: ReactNode
  windowWidth: number
  windowHeight: number
  isDesktop: boolean
  previewMode: 'desktop' | 'mobile'
}

/**
 * Provider that supplies window size information to child components
 * This allows us to override window dimensions during preview mode
 */
export const PreviewProvider: React.FC<PreviewProviderProps> = ({
  children,
  windowWidth,
  windowHeight,
  isDesktop,
  previewMode,
}) => {
  const value: PreviewContextValue = {
    windowWidth,
    windowHeight,
    isDesktop,
    previewMode,
  }

  return (
    <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>
  )
}

/**
 * Hook that returns window size information
 * Falls back to real window size if not used within PreviewProvider
 */
export const usePreviewWindowSize = () => {
  const context = useContext(PreviewContext)

  if (context) {
    // Use preview context values (potentially spoofed)
    return {
      width: context.windowWidth,
      height: context.windowHeight,
      isDesktop: context.isDesktop,
      previewMode: context.previewMode,
    }
  }

  // Fallback to real window size
  return {
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
    isDesktop: typeof window !== 'undefined' ? window.innerWidth > 768 : true,
    previewMode: 'desktop' as const,
  }
}
